import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  InputBase,
  Avatar,
  Chip,
  CircularProgress,
  Button,
  IconButton,
  Snackbar,
  Alert,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import StarIcon from '@mui/icons-material/Star';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Title } from 'react-admin';

const API_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:3000';

interface Service {
  code: string;
  name: string;
  logo?: string | null;
}

const defaultColors = [
  { bg: 'rgba(115, 103, 240, 0.12)', color: '#7367f0' },
  { bg: 'rgba(0, 207, 232, 0.12)', color: '#00cfe8' },
  { bg: 'rgba(40, 199, 111, 0.12)', color: '#28c76f' },
  { bg: 'rgba(255, 159, 67, 0.12)', color: '#ff9f43' },
  { bg: 'rgba(234, 84, 85, 0.12)', color: '#ea5455' },
];

const getDefaultColor = (code: string) => {
  const index = code?.charCodeAt(0) % defaultColors.length || 0;
  return defaultColors[index];
};

const renderServiceIcon = (service: Service, size: number = 20) => {
  if (service.logo) {
    return (
      <Avatar
        src={`${API_URL}${service.logo}`}
        sx={{ width: size + 16, height: size + 16, backgroundColor: '#161d31' }}
        variant="rounded"
      >
        {service.code?.substring(0, 2).toUpperCase()}
      </Avatar>
    );
  }

  const dc = getDefaultColor(service.code);
  return (
    <Avatar sx={{ width: size + 16, height: size + 16, backgroundColor: dc.bg, color: dc.color, fontSize: '0.75rem', fontWeight: 700 }}>
      {service.code?.substring(0, 2).toUpperCase()}
    </Avatar>
  );
};

const PopularServicesPage = () => {
  const [allServices, setAllServices] = useState<Service[]>([]);
  const [popularCodes, setPopularCodes] = useState<string[]>([]);
  const [savedCodes, setSavedCodes] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const headers = getAuthHeaders();

      const [servicesRes, popularRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/sms/services`, { headers }),
        fetch(`${API_URL}/api/admin/settings/popular-services/list`, { headers }),
      ]);

      const servicesData = await servicesRes.json();
      const popularData = await popularRes.json();

      if (servicesData.data) {
        setAllServices(servicesData.data);
      }
      const codes = popularData.popularServices || [];
      setPopularCodes(codes);
      setSavedCodes(codes);
    } catch {
      setSnackbar({ open: true, message: 'Veriler yüklenemedi', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAdd = (code: string) => {
    if (!popularCodes.includes(code)) {
      setPopularCodes([...popularCodes, code]);
    }
  };

  const handleRemove = (code: string) => {
    setPopularCodes(popularCodes.filter((c) => c !== code));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const res = await fetch(`${API_URL}/api/admin/settings/popular-services/list`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ popularServices: popularCodes }),
      });
      if (!res.ok) throw new Error();
      setSavedCodes([...popularCodes]);
      setSnackbar({ open: true, message: 'Popüler servisler kaydedildi', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Kaydetme hatası', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = JSON.stringify(popularCodes) !== JSON.stringify(savedCodes);

  const popularServices = popularCodes
    .map((code) => allServices.find((s) => s.code === code))
    .filter(Boolean) as Service[];

  const availableServices = allServices.filter(
    (s) => !popularCodes.includes(s.code) &&
      (searchTerm === '' ||
        s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.code.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress sx={{ color: '#7367f0' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Title title="Popüler Servisler" />

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <StarIcon sx={{ color: '#ff9f43', fontSize: 28 }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#d0d2d6' }}>
              Popüler Servisler
            </Typography>
            <Typography variant="body2" sx={{ color: '#676d7d' }}>
              Mobil uygulamada öne çıkacak servisleri yönetin
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchData}
            sx={{ borderColor: '#404656', color: '#d0d2d6', '&:hover': { borderColor: '#7367f0' } }}
          >
            Yenile
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving || !hasChanges}
            sx={{
              backgroundColor: '#7367f0',
              '&:hover': { backgroundColor: '#5e50ee' },
              '&.Mui-disabled': { backgroundColor: '#404656', color: '#676d7d' },
            }}
          >
            {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </Button>
        </Box>
      </Box>

      {/* Selected Popular Services */}
      <Card sx={{ mb: 3, backgroundColor: '#283046', border: '1px solid #404656' }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#d0d2d6' }}>
              Secili Servisler ({popularServices.length})
            </Typography>
            {hasChanges && (
              <Chip
                label="Kaydedilmemis degisiklikler var"
                size="small"
                sx={{ backgroundColor: 'rgba(255, 159, 67, 0.16)', color: '#ff9f43', fontSize: '0.7rem' }}
              />
            )}
          </Box>

          {popularServices.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <StarIcon sx={{ fontSize: 40, color: '#404656', mb: 1 }} />
              <Typography variant="body2" sx={{ color: '#676d7d' }}>
                Henuz popüler servis eklenmedi. Asagidan servis ekleyebilirsiniz.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              {popularServices.map((service, index) => (
                <Card
                  key={service.code}
                  sx={{
                    backgroundColor: '#161d31',
                    border: '1px solid #404656',
                    transition: 'all 0.2s',
                    '&:hover': { borderColor: '#7367f0' },
                  }}
                >
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 }, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <DragIndicatorIcon sx={{ color: '#404656', fontSize: 18 }} />
                    <Typography variant="caption" sx={{ color: '#676d7d', fontWeight: 700, minWidth: 16 }}>
                      {index + 1}
                    </Typography>
                    {renderServiceIcon(service, 18)}
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#d0d2d6', lineHeight: 1.2 }}>
                        {service.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#676d7d' }}>
                        {service.code}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => handleRemove(service.code)}
                      sx={{ ml: 1, color: '#ea5455', '&:hover': { backgroundColor: 'rgba(234, 84, 85, 0.12)' } }}
                    >
                      <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Search & Add Services */}
      <Card sx={{ backgroundColor: '#283046', border: '1px solid #404656' }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#d0d2d6', mb: 2 }}>
            Servis Ekle
          </Typography>

          {/* Search */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              px: 2,
              py: 1,
              mb: 2,
              borderRadius: 1,
              backgroundColor: '#161d31',
              border: '1px solid #404656',
            }}
          >
            <SearchIcon sx={{ color: '#676d7d', fontSize: 20, mr: 1.5 }} />
            <InputBase
              placeholder="Servis adi veya kod ile ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                color: '#d0d2d6',
                fontSize: '0.875rem',
                flex: 1,
                '& ::placeholder': { color: '#676d7d', opacity: 1 },
              }}
            />
            {searchTerm && (
              <Chip
                label={`${availableServices.length} sonuc`}
                size="small"
                sx={{ backgroundColor: 'rgba(115, 103, 240, 0.12)', color: '#7367f0', fontSize: '0.7rem', height: 22 }}
              />
            )}
          </Box>

          {/* Available Services Grid */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' },
              gap: 1,
              maxHeight: 400,
              overflowY: 'auto',
              '&::-webkit-scrollbar': { width: 6 },
              '&::-webkit-scrollbar-thumb': { backgroundColor: '#404656', borderRadius: 3 },
            }}
          >
            {availableServices.map((service) => (
              <Box
                key={service.code}
                onClick={() => handleAdd(service.code)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 1.5,
                  borderRadius: 1,
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  '&:hover': { backgroundColor: 'rgba(115, 103, 240, 0.08)' },
                }}
              >
                {renderServiceIcon(service, 16)}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, color: '#d0d2d6', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.8rem' }}
                  >
                    {service.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#676d7d', fontSize: '0.65rem' }}>
                    {service.code}
                  </Typography>
                </Box>
                <AddIcon sx={{ color: '#28c76f', fontSize: 18 }} />
              </Box>
            ))}
          </Box>

          {availableServices.length === 0 && searchTerm && (
            <Box sx={{ py: 3, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: '#676d7d' }}>
                "{searchTerm}" ile eslesen servis bulunamadi
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PopularServicesPage;
